/*
 * @Description: file content
 * @Author: ljrong
 * @Date: 2024-07-22 19:34:52
 * @LastEditors: Xiaochun liu
 * @LastEditTime: 2025-09-29 16:08:46
 */
import { get, post } from '@/utils/request'
import type { UserExtraInfo } from '@/types/user'
import type { UserLoginRequest } from '@/types/login'
import type { AgentUsageLimitReq } from '@/types'
import { useWorkspaceStoreWithout } from '@/store'
import { CHAT_INTENT} from '@/features/ai-agent/types/chat'
import type { IntentIdentifyResponse } from '@/features/ai-agent/types/chat'

// 获取手机验证码
export function getSmsCode<T>(phone: string) {
  return post<T>({
    url: '/assistant/user/getPhoneLoginVerifyCode',
    data: {
      phone,
    },
  })
}

// 根据手机验证码登录
export function loginBySmsCode<T>(data: any) {
  return post<T>({
    url: '/assistant/user/phoneLogin',
    data,
  })
}

// 获取oss配置
export function fetchOssConfig() {
  return post<{
    accessKeyId: string
    accessKeySecret: string
    bucket: string
    region: string
    securityToken: string
    uploadPath: string
    expiration: string
    uploadPathDict: {
      common: string
      comment: string
      tuningbg: string
      knowledge: string
    }
  }>({
    url: '/assistant/oss/getOssConfig',
  })
}

// 退出登录
export function logout<T>() {
  return get<T>({
    url: '/assistant/user/logout',
  })
}

// 微信用户绑定手机号
export function bingWxPhone<T>(data: { code: string; phone: string; tempToken: string; cdKey: string }) {
  return post<T>({
    url: '/assistant/user/wxBindPhone',
    data,
  })
}

// 获取用户信息
export function getUserInfo<T>(enterpriseId?: number) {
  return get<T>({
    url: enterpriseId ? `/assistant/user/info?enterpriseId=${enterpriseId}` : '/assistant/user/info',
  })
}

// 能量点到期提醒
export function getExpirationRemind<T>() {
  const workspaceStore = useWorkspaceStoreWithout()
  const isPersonal = workspaceStore.isPersonal
  return post<T>({
    url: '/assistant/amount/expirationRemind',
    data: {
      enterpriseId: isPersonal ? null : workspaceStore.workspaceId,
    },
  })
}

// 创建企业
export function createCompany<T>(data: { name: string }) {
  console.log(data, 'createCompany')
  return post<T>({
    url: '/assistant/enterprise/create',
    data,
  })
}

// 获取用户工作空间
export function getWorkspaces<T>() {
  return post<T>({
    url: '/assistant/enterprise/getWorkspace',
  })
}

// 获取用户工作空间余额
export function getDropUserInfo(data: { enterpriseId: number | null }) {
  return post({
    url: '/assistant/enterprise/amount/memberOverview',
    data,
  })
}

/**
 *
 * 获取签名后的URL
 * @returns
 */
export function getSignedUrl(url: string) {
  return post<string>({
    url: `/assistant/oss/getSignedUrl?url=${url}`,
  })
}

// 获取预览文件的凭证
export function getPreviewToken<T>(data: {
  ossUrl: string
}) {
  return post<T>({
    url: '/assistant/imm/generateWebofficeToken',
    data,
  })
}

// 刷新预览文件的凭证
export function refreshPreviewToken<T>(data: {
  accessToken: string
  refreshToken: string
}) {
  return get<T>({
    url: '/assistant/imm/refreshWebofficeToken',
    data,
  })
}

/**
 * 上传灵感申诉
 * @param data module: string 敏感词出现模块; riskWords: string[] 风险词数组
 * @returns
 */
export function appealSensitiveWord(data: { module: string; riskWords: string[] }) {
  return post({
    url: '/assistant/appeal/sensitiveWord',
    data,
  })
}

/** 保存用户信息 */
export function saveUserExtra<T>(data: UserExtraInfo) {
  return post<T>({
    url: '/assistant/user/extra/edit',
    data,
  })
}

/** 用户详细信息 */
export function getUserExtra<T>() {
  return get<T>({
    url: '/assistant/user/extra/get',
  })
}

// 对接美业数据系统
/** 获取数据系统的 token */
export function dataUserLogin<T>(data: UserLoginRequest) {
  return post<T>({
    url: '/assistant/board/data/user/login',
    data,
  })
}

/** 获取数据系统的 token
 * sysCode: string 系统编码，meiye-v2:数据大脑，np-全球新品洞察，tiktok-美业抖数分析，dssc-数选供应链系统，ci-消费者洞察系统，npen-全球新品英文版，amazon-亚马逊数据洞察，amazonen-亚马逊数据洞察英文版
 */
export function getAccessToken<T>(data: { sysCode: string }) {
  return get<T>({
    url: '/assistant/board/data/user/access',
    data,
  })
}

/** 验证美业数据系统的 token 是否有效 token: 美业数据系统颁发的 token */
export function authAccessToken<T>(data: { token: string }) {
  return get<T>({
    url: '/assistant/board/data/user/auth',
    data,
  })
}

/** 获取扫码结果 key: 获取二维码的凭证 */
export function checkgeo<T>(data: { key: string; sysCode: string }) {
  return get<T>({
    url: '/assistant/board/data/user/checkgeo',
    data,
  })
}

// 记录用户引导步骤
export function saveUserGuideStep(data: { step: number }) {
  return post({
    url: '/assistant/user/dataRecord/add',
    data: {
      dataType: 'guide',
      dataCode: 'v2',
      dataValue: data.step,
    },
  })
}

// 获取用户引导步骤
export function getUserGuideStep() {
  return post<{ dataValue: string }>({
    url: '/assistant/user/dataRecord/get',
    data: {
      dataType: 'guide',
      dataCode: 'v2',
    },
  })
}

// 获取新用户引导和引导步骤
export function getUserDataRecordList<T>(data: { dataType: string, dataCode: string }[]) {
  return post<T>({
    url: '/assistant/user/dataRecord/getList',
    data,
  })
}

// 获取首页弹窗配置接口
export function getIndexPopupConfig<T>() {
  return get<T>({
    url: '/assistant/config/indexPopup',
  })
}
// 校验登录状态
export function checkToken() {
  return get({
    url: '/assistant/auth/checkToken',
  })
}

// 宣称工具-放入token(如果插件跳转过来的时候，cowork已经登录完成)
export function putTokenByClaim(data: { claimKey: string }) {
  return post({
    url: '/assistant/auth/putTokenByClaim',
    data,
  })
}

/** 获取agent使用次数限制 */
export function getAgentUsageLimit<T>(data: AgentUsageLimitReq) {
  return post<T>({
    url: '/assistant/permission/settings/agentUsageLimit',
    data,
  })
}
/** 获取agent使用次数限制，多维报告和简单取数 */
export function getAgentUsageLimitByDataTypeList<T>(data: { dataTypeList: number[] }) {
  // ============ 测试数据 - 取消注释来使用 ============
  // REQ-01: 付费用户，仅多维报告用完 (期望: 多维报告总使用次数(50次)已用完)
  // return Promise.resolve([
  //   { dataType: 0, usedCount: 30, maxCount: 100, remainingCount: 70, hasLimit: true, limitType: 1, trialFlag: false },
  //   { dataType: 2, usedCount: 50, maxCount: 50, remainingCount: 0, hasLimit: true, limitType: 1, trialFlag: false },
  // ] as any)

  // REQ-02: 付费用户，仅简单取数用完 (期望: 简单取数总使用次数(100次)已用完)
  // return Promise.resolve([
  //   { dataType: 0, usedCount: 100, maxCount: 100, remainingCount: 0, hasLimit: true, limitType: 1, trialFlag: false },
  //   { dataType: 2, usedCount: 30, maxCount: 50, remainingCount: 20, hasLimit: true, limitType: 1, trialFlag: false },
  // ] as any)

  // REQ-03: 试用用户，仅多维报告用完 (期望: 多维报告总试用次数(5次)已用完)
  // return Promise.resolve([
  //   { dataType: 0, usedCount: 10, maxCount: 20, remainingCount: 10, hasLimit: true, limitType: 1, trialFlag: true },
  //   { dataType: 2, usedCount: 5, maxCount: 5, remainingCount: 0, hasLimit: true, limitType: 1, trialFlag: true },
  // ] as any)

  // REQ-04: 试用用户，仅简单取数用完 (期望: 简单取数总试用次数(20次)已用完)
  // return Promise.resolve([
  //   { dataType: 0, usedCount: 20, maxCount: 20, remainingCount: 0, hasLimit: true, limitType: 1, trialFlag: true },
  //   { dataType: 2, usedCount: 3, maxCount: 5, remainingCount: 2, hasLimit: true, limitType: 1, trialFlag: true },
  // ] as any)

  // REQ-05: 付费用户，两种服务均已用完 (期望: 多维报告总使用次数(50次)及简单取数总使用次数(100次)均已用完)
  // return Promise.resolve([
  //   { dataType: 0, usedCount: 100, maxCount: 100, remainingCount: 0, hasLimit: true, limitType: 1, trialFlag: false },
  //   { dataType: 2, usedCount: 50, maxCount: 50, remainingCount: 0, hasLimit: true, limitType: 1, trialFlag: false },
  // ] as any)

  // REQ-06: 试用用户，两种服务均已用完 (期望: 多维报告总试用次数(5次)及简单取数总试用次数(20次)均已用完)
  // return Promise.resolve([
  //   { dataType: 0, usedCount: 20, maxCount: 20, remainingCount: 0, hasLimit: true, limitType: 1, trialFlag: true },
  //   { dataType: 2, usedCount: 5, maxCount: 5, remainingCount: 0, hasLimit: true, limitType: 1, trialFlag: true },
  // ] as any)

  // REQ-MIXED: 混合场景，多维报告试用+简单取数付费，两种服务均已用完 
  // (期望: 多维报告总试用次数(5次)及简单取数总使用次数(100次)均已用完)
  // return Promise.resolve([
  //   { dataType: 0, usedCount: 100, maxCount: 100, remainingCount: 0, hasLimit: true, limitType: 1, trialFlag: false },
  //   { dataType: 2, usedCount: 5, maxCount: 5, remainingCount: 0, hasLimit: true, limitType: 1, trialFlag: true },
  // ] as any)
  // ============ 测试数据结束 ============

  return post<T>({
    url: '/assistant/permission/settings/agentUsageLimitList',
    data,
  })
}
// 校验聊天限制, 在发送消息前调用这个接口
export function checkChatLimit<T>(data: { content: string, type: number }) {
  // ============ 测试数据 - 取消注释来使用 ============
  // 场景1: 多维分析 - 多维报告已用完 (chatIntent: 'data_analysis')
  // return Promise.resolve({
  //   flag: false,
  //   agentUsageLimitVO: { dataType: 2, usedCount: 50, maxCount: 50, remainingCount: 0, hasLimit: true, limitType: 1, trialFlag: false },
  //   chatIntent: 'data_analysis'
  // } as any)

  // 场景2: 简单问数 - 简单取数已用完 (chatIntent: 'data_retrieval')
  // return Promise.resolve({
  //   flag: false,
  //   agentUsageLimitVO: { dataType: 0, usedCount: 100, maxCount: 100, remainingCount: 0, hasLimit: true, limitType: 1, trialFlag: false },
  //   chatIntent: 'data_retrieval'
  // } as any)

  // 场景3: 其他类型 - 简单取数已用完 (chatIntent: 'other')
  // return Promise.resolve({
  //   flag: false,
  //   agentUsageLimitVO: { dataType: 0, usedCount: 20, maxCount: 20, remainingCount: 0, hasLimit: true, limitType: 1, trialFlag: true },
  //   chatIntent: 'other'
  // } as any)

  // 场景3-配合REQ-03: 多维分析 - 多维报告已用完 (试用用户)
  // return Promise.resolve({
  //   flag: false,
  //   agentUsageLimitVO: { dataType: 2, usedCount: 5, maxCount: 5, remainingCount: 0, hasLimit: true, limitType: 1, trialFlag: true },
  //   chatIntent: 'data_analysis'
  // } as any)

  // 场景4: 两种服务都用完 (chatIntent: '')
  // return Promise.resolve({
  //   flag: false,
  //   agentUsageLimitVO: { dataType: 0, usedCount: 100, maxCount: 100, remainingCount: 0, hasLimit: true, limitType: 1, trialFlag: false },
  //   chatIntent: ''
  // } as any)

  // 场景5-混合: 配合 REQ-MIXED，两种服务都用完，chatIntent 为空
  // return Promise.resolve({
  //   flag: false,
  //   agentUsageLimitVO: { dataType: 0, usedCount: 100, maxCount: 100, remainingCount: 0, hasLimit: true, limitType: 1, trialFlag: false },
  //   chatIntent: ''
  // } as any)
  // ============ 测试数据结束 ============

  return post<T>({
    url: '/assistant/v2/agent/market/chat/checkChatLimit',
    data,
  })
}
// 修改个人信息-获取信息
export function getuserInfoRecord<T>() {
  return get<T>({
    url: '/assistant/user/userInfoRecord/getUserInfo',
  })
}
/** 修改个人信息-保存信息 */
export function saveuserInfoRecord<T>(data: { userInfo: UserExtraInfo }) {
  return post<T>({
    url: '/assistant/user/userInfoRecord/submitUserInfo',
    data,
  })
}
// 公司推荐列表
export function getCompanyNameSearchRecommend<T>(data: {
  enterpriseName: string
}) {
  return post<T>({
    url: '/assistant/user/userInfoRecord/searchEnterprise',
    data,
  })
}

/** 上传职业认证图片 */
export function uploadRegisterImg(file: File) {
  const formData = new FormData()
  formData.append('file', file)

  return post<string>({
    url: '/assistant/auth/uploadRegisterImg',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

/** 意图识别 */
export function identifyIntent<T>(data: { content: string, sourceIntent: CHAT_INTENT, sessionId: string }) {
  return post<IntentIdentifyResponse>({
    url: '/assistant/v2/agent/market/chat/detectIntent',
    data,
  })
}
